import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  BarChart3, Users, TrendingUp, TrendingDown, Heart, Target,
  Download, FileText, ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';

function MiniChart({ data, dataKey, color, height = 48 }) {
  if (!data || data.length === 0) return null;
  const values = data.map(d => d[dataKey] || 0);
  const max = Math.max(...values, 1);
  const barWidth = Math.max(4, Math.min(16, Math.floor(300 / values.length) - 2));
  return (
    <div className="flex items-end gap-[2px]" style={{ height }}>
      {values.map((v, i) => (
        <div
          key={`item-${i}`}
          className={`rounded-t-sm transition-all bg-${color}-400 hover:bg-${color}-500`}
          style={{ width: barWidth, height: `${Math.max(2, (v / max) * height)}px` }}
          title={`${data[i]?.date}: ${v}`}
        />
      ))}
    </div>
  );
}

function CompareMetric({ label, current, previous, unit = '', icon: Icon, color }) {
  const diff = previous > 0 ? ((current - previous) / previous * 100).toFixed(0) : current > 0 ? 100 : 0;
  const isUp = diff > 0;
  const isDown = diff < 0;
  return (
    <div className="p-4 rounded-xl bg-white border border-border/50">
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={16} className={`text-${color}-600`} />}
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
      </div>
      <div className="flex items-end gap-2">
        <span className="text-2xl font-bold">{current}{unit}</span>
        <div className={`flex items-center gap-0.5 text-xs font-medium ${isUp ? 'text-emerald-600' : isDown ? 'text-red-500' : 'text-slate-400'}`}>
          {isUp ? <ArrowUpRight size={12} /> : isDown ? <ArrowDownRight size={12} /> : <Minus size={12} />}
          {Math.abs(diff)}%
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground mt-1">vs previous: {previous}{unit}</p>
    </div>
  );
}

export default function AnalyticsPage() {
  const { user, axiosAuth } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState(null);
  const [trendDays, setTrendDays] = useState('30');
  const [exporting, setExporting] = useState(false);
  const api = axiosAuth();
  const isAdmin = user.role === 'admin';

  useEffect(() => {
    api.get('/analytics/overview').then(r => setAnalytics(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/analytics/trends?days=${trendDays}`).then(r => setTrends(r.data)).catch(() => {});
  }, [trendDays]);

  const downloadGrantReport = async () => {
    setExporting(true);
    try {
      const r = await api.get('/analytics/grant-report', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `grant_report_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Grant report downloaded!');
    } catch { toast.error('Export failed'); }
    setExporting(false);
  };

  const downloadExport = async () => {
    try {
      const r = await api.get('/analytics/export');
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_export_${new Date().toISOString().slice(0, 10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Analytics data exported!');
    } catch { toast.error('Export failed'); }
  };

  if (!analytics) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin" /></div>;

  const metrics = [
    { label: 'Total Clients', value: analytics.total_clients, icon: Users, color: 'teal' },
    { label: 'Peer Specialists', value: analytics.total_specialists, icon: Heart, color: 'sky' },
    { label: 'Total Check-ins', value: analytics.total_checkins, icon: TrendingUp, color: 'amber' },
    { label: 'Total Appointments', value: analytics.total_appointments, icon: Target, color: 'violet' },
    { label: 'Session Notes', value: analytics.total_sessions, icon: BarChart3, color: 'emerald' },
    { label: 'Active Flags', value: analytics.active_flags, icon: Target, color: 'red' },
  ];

  const tw = trends?.comparison?.this_week || {};
  const lw = trends?.comparison?.last_week || {};

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Analytics & Reports</h1>
          <p className="text-muted-foreground text-sm mt-1">Program performance overview and data exports</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={downloadExport} className="rounded-full gap-1.5 h-9 text-xs"><Download size={14} /> Export JSON</Button>
            <Button onClick={downloadGrantReport} disabled={exporting} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-1.5 h-9 text-xs" data-testid="download-grant-report">
              <FileText size={14} /> {exporting ? 'Generating...' : 'Grant Report CSV'}
            </Button>
          </div>
        )}
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {metrics.map(m => (
          <Card key={m.label} className="border-0 shadow-sm rounded-2xl"><CardContent className="p-4">
            <div className={`w-8 h-8 rounded-lg bg-${m.color}-50 flex items-center justify-center mb-2`}><m.icon className={`w-4 h-4 text-${m.color}-600`} /></div>
            <p className="text-2xl font-bold">{m.value}</p>
            <p className="text-xs text-muted-foreground">{m.label}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Week-over-Week Comparison */}
      {trends && (
        <div>
          <h2 className="text-base font-semibold mb-3" style={{ fontFamily: 'Manrope' }}>This Week vs Last Week</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <CompareMetric label="Check-ins" current={tw.checkins || 0} previous={lw.checkins || 0} icon={BarChart3} color="teal" />
            <CompareMetric label="Avg Mood" current={tw.avg_mood || 0} previous={lw.avg_mood || 0} icon={Heart} color="rose" />
            <CompareMetric label="Sobriety Rate" current={tw.sobriety_rate || 0} previous={lw.sobriety_rate || 0} unit="%" icon={Target} color="emerald" />
            <CompareMetric label="Employment Rate" current={tw.employment_rate || 0} previous={lw.employment_rate || 0} unit="%" icon={TrendingUp} color="sky" />
          </div>
        </div>
      )}

      {/* Trend Charts */}
      {trends?.daily?.length > 0 && (
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Daily Trends</CardTitle>
              <Select value={trendDays} onValueChange={setTrendDays}>
                <SelectTrigger className="w-[120px] h-8 rounded-full text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 Days</SelectItem>
                  <SelectItem value="14">14 Days</SelectItem>
                  <SelectItem value="30">30 Days</SelectItem>
                  <SelectItem value="90">90 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Daily Check-ins</p>
                <MiniChart data={trends.daily} dataKey="checkins" color="teal" height={56} />
                <div className="flex justify-between text-[9px] text-muted-foreground mt-1">
                  <span>{trends.daily[0]?.date?.slice(5)}</span>
                  <span>{trends.daily[trends.daily.length - 1]?.date?.slice(5)}</span>
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Average Mood</p>
                <MiniChart data={trends.daily} dataKey="avg_mood" color="rose" height={56} />
                <div className="flex justify-between text-[9px] text-muted-foreground mt-1">
                  <span>{trends.daily[0]?.date?.slice(5)}</span>
                  <span>{trends.daily[trends.daily.length - 1]?.date?.slice(5)}</span>
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Sobriety Rate (%)</p>
                <MiniChart data={trends.daily} dataKey="sobriety_rate" color="emerald" height={56} />
                <div className="flex justify-between text-[9px] text-muted-foreground mt-1">
                  <span>{trends.daily[0]?.date?.slice(5)}</span>
                  <span>{trends.daily[trends.daily.length - 1]?.date?.slice(5)}</span>
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Active Users</p>
                <MiniChart data={trends.daily} dataKey="unique_users" color="sky" height={56} />
                <div className="flex justify-between text-[9px] text-muted-foreground mt-1">
                  <span>{trends.daily[0]?.date?.slice(5)}</span>
                  <span>{trends.daily[trends.daily.length - 1]?.date?.slice(5)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Wellness Indicators */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-3"><CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Wellness Indicators</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1"><span className="text-sm text-muted-foreground">Average Mood Score</span><span className="text-sm font-semibold">{analytics.avg_mood}/10</span></div>
                <div className="h-3 bg-muted rounded-full overflow-hidden"><div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${analytics.avg_mood * 10}%` }} /></div>
              </div>
              <div>
                <div className="flex justify-between mb-1"><span className="text-sm text-muted-foreground">Average Cravings</span><span className="text-sm font-semibold">{analytics.avg_cravings}/10</span></div>
                <div className="h-3 bg-muted rounded-full overflow-hidden"><div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${analytics.avg_cravings * 10}%` }} /></div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-3"><CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Engagement Summary</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-border/50"><span className="text-sm text-muted-foreground">Client-to-Specialist Ratio</span><span className="text-sm font-semibold">{analytics.total_specialists > 0 ? (analytics.total_clients / analytics.total_specialists).toFixed(1) : 'N/A'}:1</span></div>
              <div className="flex justify-between items-center py-2 border-b border-border/50"><span className="text-sm text-muted-foreground">Avg Check-ins per Client</span><span className="text-sm font-semibold">{analytics.total_clients > 0 ? (analytics.total_checkins / analytics.total_clients).toFixed(1) : 0}</span></div>
              <div className="flex justify-between items-center py-2"><span className="text-sm text-muted-foreground">Avg Sessions per Client</span><span className="text-sm font-semibold">{analytics.total_clients > 0 ? (analytics.total_sessions / analytics.total_clients).toFixed(1) : 0}</span></div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
